export default function useUndoRedo() {
  return {
    onUndoRedoHander, updateUndoRedoData, setDataAndPointer,
    clearAllPreviousHistory
  }
}

function onUndoRedoHander(taskId, historyPrefix, action) {
  const localStoredData = getStoredUndoData(taskId, historyPrefix)
  const tempData = localStoredData?.contentHistory
  const pointer = localStoredData?.pointer

  const historyLength = tempData.length
  const updatedPointer = getUndoRedoPointer(pointer, action, historyLength)

  const noData = !tempData[updatedPointer]
  if (noData) {
    console.warn("No such data in pointer. Aborting undo process.")
    return { noPointer: true }
  }

  updateUndoRedoData(tempData, updatedPointer, taskId, historyPrefix)

  return {
    undoRedoPressed: true,
    description: tempData[updatedPointer].content
  }
}

function updateUndoRedoData(data, pointer, taskId, historyPrefix) {
  const storageData = data
  const currPointer = pointer
  try {
    localStorage.setItem(
      historyPrefix + [taskId],
      JSON.stringify({ contentHistory: storageData, pointer: currPointer })
    )
  } catch (e) {
    if (e.code === 22 && e.name === "QuotaExceededError") {
      clearAllHistory(historyPrefix)
      const lastUpdate = data[data.length - 1]
      const history = [lastUpdate]
      updateUndoRedoData(history, currPointer, taskId, historyPrefix)
    }
  }
}

function setDataAndPointer(maxUndoSteps, taskId, taskHistoryPrefix) {
  const localStoredData = getStoredUndoData(taskId, taskHistoryPrefix)
  const initialData = localStoredData?.contentHistory ?? []
  const initialPointer = localStoredData?.pointer
  const hasNoDataOrPointer = !(initialData.length && initialPointer >= 0)
  if (hasNoDataOrPointer) {
    console.warn("No data or no pointer", initialData, initialPointer)
    return { data: [], pointer: 0 }
  }

  const updatedData = reduceDataLengthToMaxSteps(initialData, maxUndoSteps)
  // * When adding a new step (which is done while calling this method `setDataAndPointer`)
  // * we should always set the pointer to the last available step.
  // * For other values `getUndoRedoPointer` does the job when switching between states.
  const updatedPointer = updatedData.length 

  return {
    data: updatedData,
    pointer: updatedPointer
  }
}

function clearAllPreviousHistory(currentItemId) {
  Object.keys(localStorage)
    .filter(key =>
      acceptHasString(key, 'history')
      && rejectHasString(key, currentItemId)
    )
    .forEach(key => localStorage.removeItem(key))

  function acceptHasString(element, string) {
    return element.toLocaleLowerCase().indexOf(string) >= 0
  }

  function rejectHasString(element, string) {
    return element.toLocaleLowerCase().indexOf(string) < 0
  }
}

// * PRIVATE FUNCTIONS

function getStoredUndoData(taskId, taskHistoryPrefix) {
  const itemKey = taskHistoryPrefix + [taskId]
  const dataString = localStorage.getItem(itemKey) || "[]"
  return JSON.parse(dataString)
}

function getUndoRedoPointer(pointer, action, contentHistoryLength) {
  if (action === "undo") {
    return pointer <= 0 ? 0 : pointer - 1
  }

  if (action === "redo") {
    const maxIndex = contentHistoryLength - 1
    return pointer >= maxIndex ? pointer : pointer + 1
  }

  return null
}

function clearAllHistory(historyPrefix) {
  Object.keys(localStorage)
    .filter(key => key.indexOf(historyPrefix) >= 0)
    .forEach(key => localStorage.removeItem(key))
}

function reduceDataLengthToMaxSteps(data, maxUndoSteps) {
  const hasTooManySteps = data.length > maxUndoSteps
  if (hasTooManySteps) {
    data.shift()
    return reduceDataLengthToMaxSteps(data, maxUndoSteps)
  }

  return data
}

